import { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { flatJoin } from 'shared/util'

import { validationMessages } from 'constants/validation'
import {
	FormControl, FormControlLabel, FormHelperText,
	InputAdornment, OutlinedInput,
	Radio, RadioGroup, ToggleButton, ToggleButtonGroup, 
	Stack, StackProps, Box, Typography	
} from '@mui/material'

import { Section } from 'components/controls/section'
import { OptionSelect } from 'components/controls/optionSelect'

import { WeekDay, WeekDays } from 'shared/weekDay'
import {
	PredictionCondition, PredictionConditionLv,
	SpecialProducts
} from 'interface/carenet/prediction'

import { ProductResult } from 'interface/carenet/product'

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
	productId: yup.array()
		.of(yup.number().required(Required)),
	reservationDays: yup.number().required(Required),
	broadcastWeekday: yup.string().required(Required),
	broadcastHour: yup.number().required(Required),
	broadcastDuration: yup.number().required(Required),
	reservationUp: yup.number().required(Required),
	viewUp: yup.number().required(Required),
	isAreaLec: yup.number().nullable(),
	providerCode: yup.string().nullable()
})
type FormType = yup.InferType<typeof formSchema>

type useLvPredictionFormProps = {
	productList: ProductResult[]
	condition: PredictionCondition | null
}
export const useLvPredictionForm = ({
	productList,
	condition
}: useLvPredictionFormProps) => {
	const lvCondition = condition?.source === 'lv' ? condition as PredictionConditionLv : null

	const form = useForm({
		defaultValues: lvCondition ? {
			productId: lvCondition.productId ?? [],
			reservationDays: lvCondition.reservationDays ?? undefined,
			broadcastWeekday: lvCondition.broadcastWeekday ?? undefined,
			broadcastHour: lvCondition.broadcastHour ?? undefined,
			broadcastDuration: lvCondition.broadcastDuration ?? undefined,
			reservationUp: lvCondition.reservationUp ?? undefined,
			viewUp: lvCondition.viewUp ?? undefined,
			isAreaLec: lvCondition.isAreaLec === true ? 1 : lvCondition?.isAreaLec === false ? 0 : undefined,
			providerCode: lvCondition.providerCode ?? undefined
		} : {
			productId: [],
			reservationDays: 30,
			broadcastWeekday: Object.keys(WeekDays)[(new Date()).getDay()] as WeekDay,
			broadcastHour: 19,
			broadcastDuration: 60,
			reservationUp: 60,
			viewUp: 600
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	const submit = useCallback(
		(input: FormType) => {
			const condition: PredictionConditionLv = {
				source: 'lv',
				productId: (input.productId && input.productId.length > 0) ? input.productId : undefined,
				reservationDays: input.reservationDays,
				broadcastWeekday: input.broadcastWeekday ? (input.broadcastWeekday as WeekDay) : undefined,
				broadcastHour: input.broadcastHour,
				broadcastDuration: input.broadcastDuration,
				reservationUp: input.reservationUp,
				viewUp: input.viewUp,
				isAreaLec: input.isAreaLec === 1 ? true : input.isAreaLec === 0 ? false : undefined,
				providerCode: (input.providerCode && input.providerCode.length > 0) ? input.providerCode : undefined
			}
			condition.hash = JSON.stringify(condition)
			condition.text = flatJoin(', ',
				condition.productId?.flatMap(productId =>
					SpecialProducts.find(product => product.productId === productId)?.productName ??
					productList.find(product => product.productId === productId)?.productName ?? []
				).join(', '),
				condition.broadcastWeekday ? WeekDays[condition.broadcastWeekday].long : undefined,
				condition.broadcastHour ? `${condition.broadcastHour}時` : undefined,
				condition.broadcastDuration ? `${condition.broadcastDuration}分間` : undefined,
				condition.reservationDays ? `${condition.reservationDays}日前予約` : undefined,
				condition.reservationUp ? `予約時${condition.reservationUp}UP` : undefined,
				condition.viewUp ? `視聴時${condition.reservationUp}UP` : undefined,
				condition.isAreaLec === true ? 'エリア講演会' : condition.isAreaLec === false ? '全国講演会' : undefined,
				condition.providerCode ? `配信ベンダー=${condition.providerCode}` : undefined
			)
			return condition
		},
		[productList]
	)
	return {
		productList,
		form,
		submit
	}
}

export const LvPredictionForm = ({
	form: {
    control,
    formState: { errors }
  },
	productList,
	...others
}: ReturnType<typeof useLvPredictionForm> & StackProps) => {
	return (
		<Stack direction="column" {...others}>
			<Box>
				<Section label="対象製品" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="productId"
						render={({field}) => (
							<>
								<OptionSelect
									{...field}
									variant="outlined"
									size="small"
									optionList={productList.map((product) => ({
										id: product.productId,
										label: product.productName
									})).concat(SpecialProducts.map(product => ({
										id: product.productId,
										label: product.productName
									})))}
								/>
								{errors.productId && (
									<FormHelperText error>{errors.productId.message}</FormHelperText>
								)}
							</>
						)}
					/>  
				</Section>

				<Section label="配信予定" labelProps={{ py: 2 }} innerProps={{ spacing: 2 }}>
					<Controller
						control={control}
						name="broadcastWeekday"
						render={({ field }) => (
							<ToggleButtonGroup
								aria-label="broadcastWeekday"
								exclusive
								color="primary"
								size="small"
								{...field}
							>
								{Object.entries(WeekDays).map(([key, label]) => (
									<ToggleButton key={`broadcastWeekday-${key}`} value={key} >{label.long}</ToggleButton>
								))}
							</ToggleButtonGroup>
						)}
					/>
					{errors.broadcastWeekday && (
						<FormHelperText error>{errors.broadcastWeekday.message}</FormHelperText>
					)}
					<Stack direction="row" spacing={1} alignItems="center">
						<Controller
							control={control}
							name="broadcastHour"
							render={({ field }) => (
								<FormControl sx={{ maxWidth: '6em' }} variant="outlined">
									<OutlinedInput
										{...field}
										endAdornment={<InputAdornment position="end">時</InputAdornment>}
										size="small"
										error={!!errors.broadcastHour}
									/>
								</FormControl>
							)}
						/>
						<Typography variant='body2' whiteSpace='nowrap'>から</Typography>
						<Controller
							control={control}
							name="broadcastDuration"
							render={({ field }) => (
								<FormControl sx={{ maxWidth: '7em' }} variant="outlined">
									<OutlinedInput
										{...field}
										endAdornment={<InputAdornment position="end">分間</InputAdornment>}
										size="small"
										error={!!errors.broadcastDuration}
									/>
								</FormControl>
							)}
						/>
					</Stack>
					{errors.broadcastHour && (
						<FormHelperText error>{errors.broadcastHour.message}</FormHelperText>
					)}
					{errors.broadcastDuration && (
						<FormHelperText error>{errors.broadcastDuration.message}</FormHelperText>
					)}
				</Section>

				<Section label="予約期間" labelProps={{ py: 2 }} innerProps={{ py: 0, my: 0 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography py={2} variant='body2' whiteSpace='nowrap'>配信日の</Typography>
						<Controller
							control={control}
							name="reservationDays"
							render={({ field }) => (
								<FormControl sx={{ maxWidth: '7em' }} variant="outlined">
									<OutlinedInput
										{...field}
										endAdornment={<InputAdornment position="end">日前</InputAdornment>}
										size="small"
										error={!!errors.reservationDays}
									/>
								</FormControl>
							)}
						/>
						<Typography py={2} variant='body2' whiteSpace='nowrap'>から</Typography>
					</Stack>
					{errors.reservationDays && (
						<FormHelperText error>{errors.reservationDays.message}</FormHelperText>
					)}
				</Section>

				<Section label="付与UP" labelProps={{ py: 2 }} disablePaddingY>
					<Stack direction="row" spacing={2} alignItems="center">
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography py={2} variant='body2' whiteSpace='nowrap'>予約時</Typography>
							<Controller
								control={control}
								name="reservationUp"
								render={({ field }) => (
									<FormControl sx={{ maxWidth: '6em' }} variant="outlined">
										<OutlinedInput
											{...field}
											endAdornment={<InputAdornment position="end">UP</InputAdornment>}
											size="small"
											error={!!errors.reservationUp}
										/>
									</FormControl>
								)}
							/>
						</Stack>
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography py={2} variant='body2' whiteSpace='nowrap'>視聴時</Typography>
							<Controller
								control={control}
								name="viewUp"
								render={({ field }) => (
									<FormControl sx={{ maxWidth: '6em' }} variant="outlined">
										<OutlinedInput
											{...field}
											endAdornment={<InputAdornment position="end">UP</InputAdornment>}
											size="small"
											error={!!errors.viewUp}
										/>
									</FormControl>
								)}
							/>
						</Stack>
					</Stack>

					{errors.reservationUp && (
						<FormHelperText error>{errors.reservationUp.message}</FormHelperText>
					)}
					{errors.viewUp && (
						<FormHelperText error>{errors.viewUp.message}</FormHelperText>
					)}
				</Section>

				<Section label="案件種別" labelProps={{ py: 2 }} disablePaddingY>
					<Controller
						control={control}
						name="isAreaLec"
						render={({ field }) => (
							<FormControl>
								<RadioGroup
									{...field}
									row
								>
									<FormControlLabel value={0} control={<Radio/>} label="全国講演会" />
									<FormControlLabel value={1} control={<Radio/>} label="エリア講演会" />
								</RadioGroup>
							</FormControl>
						)}
					/>
					{errors.isAreaLec && (
						<FormHelperText error>{errors.isAreaLec.message}</FormHelperText>
					)}
				</Section>

				{/*
				<Section label="配信ベンダー" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="providerCode"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.providerCode}
								helperText={errors.providerCode ? errors.providerCode.message : ''}
							/>
						)}
					/>
					{errors.providerCode && (
						<FormHelperText error>{errors.providerCode.message}</FormHelperText>
					)}
				</Section>
				*/}
			</Box>
		</Stack>
	)
}